//hooks
import { useState, useEffect, useRef } from "react"
import { useApi } from "../../../hooks/useApi"

//api
import { GetCarsOffersApi } from "../../../api/Cars/GetCarsOffersApi"

//mui
import { Box, Card, CardContent, CircularProgress, DialogProps, Typography } from "@mui/material";

//mui icons
import ReplayIcon from '@mui/icons-material/Replay';
import PercentIcon from '@mui/icons-material/Percent';

//type
import { carType } from "../../../api/Cars/type/CarsType";

//component
import NoData from "../../../component/NoData";
import CarCard from "../../../component/carCard/CarCard";
import CarDetails from "../../../popup/carDetails/CarDetails";

//image
import carPlaceHolder from "../../../assets/carPlaceHolder.png"

//translate
import { useTranslation } from "react-i18next";
import i18n from "../../../translate/i18n";

interface CarsOffersParamsType {
  page: number;
  per_page: number;
}

const CarsOffers = () => {
    const { t } = useTranslation();
    const [page, setPage] = useState<number>(1);
    const [lastPage, setLastPage] = useState<number>(1);
    const carsPerPage = 6;
    const [initialLoading, setInitialLoading] = useState<boolean>(true);
    const [initialError, setInitialError] = useState<boolean>(false);
    const [carsOffersData, setCarsOffersData] = useState<carType[]>([]);
    const loaderRef = useRef<HTMLDivElement | null>(null);
    const [selectedCar, setSelectedCar] = useState<carType | null>(null);
    const [openCarDetails, setOpenCarDetails] = useState<{open:boolean,scrollType: DialogProps['scroll']}>({
        open:false,
        scrollType: "paper",
    });
    const {
        loading: carsOfferLoading,
        error: carsOffersError,
        HandlerApi: getCarsOffersApi
    } = useApi(GetCarsOffersApi, "GET");
    const fetchBookings = async () => {
        const params: CarsOffersParamsType = {
        page: page,
        per_page: carsPerPage
        };
        if(page === 1) {
        setInitialLoading(true)
        }
        try {
        const resultBookingCars = await getCarsOffersApi(params);
        setLastPage(resultBookingCars.data.last_page);
        setCarsOffersData((prev: any) => {
            const newCars = resultBookingCars.data.data;
            const filteredPrev = prev.filter((car:any) => !newCars.some((element:any) => element.id === car.id));
            const combined = [...filteredPrev, ...newCars];
            return combined;
        });
        setInitialLoading(false);
        setInitialError(false);
        }
        catch (error) {
        setInitialLoading(false);
        if (page === 1) {
            setInitialError(true);
        }
        }
        finally {
        setInitialLoading(false);
        }
    };
    useEffect(() => {
    const fetchData = async (page: number) => {
        const params: CarsOffersParamsType = {
        page,
        per_page: carsPerPage,
        };
        try {
        const resultBookingCars = await getCarsOffersApi(params);
        setLastPage(resultBookingCars.data.last_page);
        setCarsOffersData((prev: any) => {
            const newCars = resultBookingCars.data.data;
            const filteredPrev = prev.filter((car:any) => !newCars.some((element:any) => element.id === car.id));
            const combined = [...filteredPrev, ...newCars];
            return combined;
        });
        setInitialLoading(false);
        setInitialError(false);
        }
        catch (error) {
        setInitialLoading(false);
        if (page === 1) {
            setInitialError(true);
        }
        else {
            setInitialError(false);
        }
        }
        finally {
        setInitialLoading(false);
        }
    };
    fetchData(page);
    }, [page]);

    useEffect(() => {
    if (!loaderRef.current){
        return;
    }
    else {
        // It watches an element and tells you:“Is this element visible on the screen right now?”
        const observer = new IntersectionObserver((entries) => {
            const firstEntry = entries[0];
            if (
            firstEntry.isIntersecting &&
            !carsOfferLoading &&
            page < lastPage &&
            !carsOffersError

            ) {
            setPage((prev) => prev + 1);
            }
        });
        //tells the Intersection Observer:“Watch this element”
        observer.observe(loaderRef.current);
        return () => {
        observer.disconnect();
        };
    }
    }, [carsOfferLoading]); 

    return (
    <div>
        <Box>
            <Card className="!bg-customerColor !text-white mb-5">
                <CardContent className="!flex items-center gap-5">
                <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 14 }}>
                    <PercentIcon className="!text-white !text-[50px]"/>
                </Typography>
                <div>
                    <Typography variant="h5" component="div">
                    {t("offers_count")}
                    </Typography>
                    <Typography variant="body2">
                    {carsOfferLoading ? 0 : carsOffersData?.length} - {t("car")}
                    </Typography>
                </div>
                </CardContent>
            </Card>
            <div>
                {initialLoading
                ?
                <div className='flex justify-center'>
                    <CircularProgress/>
                </div>
                :
                <div>
                    {initialError //bookingsError
                    ?
                    <div className='cursor-pointer flex justify-center'>
                        <ReplayIcon onClick={()=>{
                        setPage(1)
                        setCarsOffersData([])
                        fetchBookings()
                        }} className='!text-[40px]'/>
                    </div>
                    : 
                    carsOffersData.length !== 0
                    ?
                    <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: "20px" }}>
                        {
                        carsOffersData.map((element, index) => (
                            <div className='cursor-pointer'
                            key={index}
                            onClick={() => {setOpenCarDetails({open: true, scrollType: "paper"}); setSelectedCar(element)}}
                            >
                                <CarCard key={index} element={element}/>
                            </div>
                        ))
                        }
                    </div>
                    {page < lastPage && <div ref={loaderRef} style={{ height: "10px" }} />}
                    {carsOfferLoading && (
                        <div className="flex justify-center mt-4">
                        <CircularProgress size={32}/>
                        </div>
                    )}
                    {carsOffersError && (
                        <div className=" cursor-pointer flex justify-center mt-4">
                        <ReplayIcon onClick={()=>{
                            fetchBookings()
                            }} className='!text-[40px]'/>
                        </div>
                    )}
                    </>
                    :<NoData title={t("bookings")} description={t("no_bookings_found")} image={carPlaceHolder}/>
                }
                </div>
                }
            </div>
            {selectedCar && (
            <CarDetails
                selectedCar={selectedCar}
                setSelectedCar={setSelectedCar}
                openCarDetails={openCarDetails}
                setOpenCarDetails={setOpenCarDetails}
            />
            )} 
        </Box>
    </div>
    )
}

export default CarsOffers